// @ts-nocheck
import { forwardRef, useMemo } from "react";
import "./PdfComprovantes.css";

const fmtBRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});
const money = (v) => (v == null ? "—" : fmtBRL.format(Number(v) || 0));
const onlyDigits = (s) => String(s ?? "").replace(/\D/g, "");

const maskCNPJ = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.replace(/^(\d{2})(\d+)/, "$1.$2");
  if (d.length <= 8) return d.replace(/^(\d{2})(\d{3})(\d+)/, "$1.$2.$3");
  if (d.length <= 12)
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, "$1.$2.$3/$4");
  return d.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2}).*/,
    "$1.$2.$3/$4-$5",
  );
};

const normalizeTipoConta = (v) => {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  if (!s) return "—";
  if (["1", "01", "cc", "corrente", "conta corrente"].includes(s))
    return "Corrente";
  if (
    [
      "2",
      "02",
      "cp",
      "poupanca",
      "poupança",
      "conta poupanca",
      "conta poupança",
    ].includes(s)
  )
    return "Poupança";
  if (
    [
      "3",
      "03",
      "salario",
      "salário",
      "conta salario",
      "conta salário",
    ].includes(s)
  )
    return "Salário";
  return v;
};

const PdfComprovantes = forwardRef(function PdfComprovantes(
  {
    logoSrc,
    dadosSindicato = {},
    dadosBancarios = {},
    descricaoDespesas = [],
    textos = {},
    resumoValores = {},
  },
  ref,
) {
  const year = new Date().getFullYear();

  const headerText =
    textos.header ?? "Apoio para Sindicatos Empresariais filiados à FIEMG";
  const introText =
    textos.intro ??
    `Apoio ao Sindicato referente ao Programa de Meritocracia Sindial ${year}.`;
  const solicitacaoRefText = textos.solicitacaoRef ?? null;
  const dataEmissaoText =
    textos.dataEmissao ??
    `Belo Horizonte, ${new Date().toLocaleDateString("pt-BR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;

  const razaoSocial =
    dadosSindicato?.razaoSocial ??
    dadosSindicato?.nome ??
    dadosSindicato?.nomeSindicato ??
    "—";

  const cnpjRaw = dadosSindicato?.cnpj ?? dadosSindicato?.cnpjSindicato ?? "";
  const cnpj = cnpjRaw ? maskCNPJ(cnpjRaw) : "—";

  const presidenteNome =
    dadosSindicato?.presidenteNome ??
    dadosSindicato?.nomePresidente ??
    dadosSindicato?.presidente ??
    "—";

  const tipoConta = normalizeTipoConta(
    dadosBancarios?.tipoConta ?? dadosBancarios?.tipo ?? "",
  );
  const banco = dadosBancarios?.banco ?? dadosBancarios?.codigoBanco ?? "—";
  const agencia = dadosBancarios?.agencia ?? "—";
  const conta = dadosBancarios?.conta ?? "—";

  const linhasDespesas = useMemo(() => {
    const arr = Array.isArray(descricaoDespesas) ? descricaoDespesas : [];
    return arr.map((d) => ({
      descricao: d?.descricao ?? d?.titulo ?? "—",
      valor: Number(d?.valor ?? d?.valorComprovado ?? 0),
    }));
  }, [descricaoDespesas]);

  const totalDespesas = useMemo(() => {
    return linhasDespesas.reduce(
      (acc, x) => acc + (Number.isFinite(x.valor) ? x.valor : 0),
      0,
    );
  }, [linhasDespesas]);

  const totalComprovado =
    resumoValores?.totalComprovado != null
      ? resumoValores.totalComprovado
      : totalDespesas;

  const totalQueSeraApoiado =
    resumoValores?.totalQueSeraApoiado != null
      ? resumoValores.totalQueSeraApoiado
      : totalComprovado;

  return (
    <div className="pdf-a4 compact" ref={ref}>
      <table className="sheet">
        <thead>
          <tr className="row-top">
            <th colSpan={3} className="sheet-title">
              {headerText}
            </th>
            <th className="sheet-logo">
              <div className="sheet-logo-wrap">
                {!!logoSrc && <img src={logoSrc} alt="FIEMG" />}
                {!!solicitacaoRefText && (
                  <div className="sheet-solicitacao-ref">{solicitacaoRefText}</div>
                )}
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr className="section">
            <th colSpan={4}>DADOS DO SINDICATO</th>
          </tr>
          <tr>
            <td className="cell" colSpan={3}>
              <div className="label">Razão Social</div>
              <div className="value">{razaoSocial}</div>
            </td>
            <td className="cell">
              <div className="label">CNPJ</div>
              <div className="value">{cnpj}</div>
            </td>
          </tr>

          <tr className="section">
            <th colSpan={4}>INFORMAÇÕES COMPLETAS PARA O REEMBOLSO</th>
          </tr>
          <tr>
            <td className="cell" colSpan={4}>
              <div className="label label--gap">Descrição completa da ação</div>
              <div className="value value--spaced">{introText}</div>
            </td>
          </tr>

          <tr className="section">
            <th colSpan={4}>DADOS BANCÁRIOS DO SINDICATO</th>
          </tr>
          <tr>
            <td className="cell">
              <div className="label">Tipo de conta</div>
              <div className="value">{tipoConta}</div>
            </td>
            <td className="cell">
              <div className="label">Banco</div>
              <div className="value">{banco}</div>
            </td>
            <td className="cell">
              <div className="label">Agência</div>
              <div className="value">{agencia}</div>
            </td>
            <td className="cell">
              <div className="label">Conta</div>
              <div className="value">{conta}</div>
            </td>
          </tr>

          <tr className="section">
            <th colSpan={4}>DESCRIÇÃO DAS DESPESAS</th>
          </tr>

          {linhasDespesas.length ? (
            linhasDespesas.map((d, i) => (
              <tr key={`${i}-${d.descricao}`}>
                <td className="cell desc" colSpan={3}>
                  <div className="value">
                    {i + 1}. {d.descricao}
                  </div>
                </td>
                <td className="cell num">
                  <div className="value">{money(d.valor)}</div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="cell" colSpan={4}>
                <div className="muted">Sem despesas informadas.</div>
              </td>
            </tr>
          )}

          <tr>
            <td className="cell total-label" colSpan={3}>
              Total das despesas
            </td>
            <td className="cell num">
              <div className="value strong">{money(totalComprovado)}</div>
            </td>
          </tr>
          <tr>
            <td className="cell total-label" colSpan={3}>
              Total que será apoiado
            </td>
            <td className="cell num">
              <div className="value strong">{money(totalQueSeraApoiado)}</div>
            </td>
          </tr>

          <tr className="section">
            <th colSpan={4}>ASSINATURA</th>
          </tr>
          <tr>
            <td className="cell" colSpan={4}>
              <div className="assinatura-linha" />
              <div className="assinatura-legenda">
                Assinatura do Presidente / Representante Legal
              </div>

              <div className="assinatura-grid">
                <div>
                  <div className="label">Nome do Presidente</div>
                  <div className="value">{presidenteNome}</div>
                </div>
                <div>
                  <div className="label">CNPJ</div>
                  <div className="value">{cnpj}</div>
                </div>
                <div className="cnpj">
                  <div className="label">Razão Social</div>
                  <div className="value">{razaoSocial}</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="sheet-data-emissao">{dataEmissaoText}</div>
    </div>
  );
});

export default PdfComprovantes;
